import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';
import { offerSaveRescue } from '../utils/strokeOfLuckPrompt';
import { rollSkillWithDiceBox } from '../attributes/Skills';
import { Dices } from 'lucide-react';
import { groupCheckLine, rollRequestLabel, rollRequestLogLine } from '../../../shared/rules/rollRequests.mjs';
import './RollRequests.scss';

/**
 * A DM roll request for this character: rolled on the sheet with the numbers the server fixed (bonus, Advantage, spell
 * dice), then sent as dice evidence. A failed roll can be rescued (Stroke of Luck, Indomitable on saves, Heroic
 * Inspiration, Boon of Fate) before it is sent, or after the result with a hidden DC. A retry resends the same dice.
 * With Boon of Fate, the sheet also offers Improve Fate (+2d4) on another character's failed roll in an open request;
 * when another character changes this character's roll, the sheet notes it and logs it.
 */
export default function RollRequestPrompt({ campaignId, characterId, character }) {
  const [requests, setRequests] = useState([]), [helpable, setHelpable] = useState([]), [dismissed, setDismissed] = useState([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const evidence = useRef(new Map()), lock = useRef(false), helpSeen = useRef(null);
  const base = `/campaigns/${encodeURIComponent(campaignId || '')}/roll-requests`;
  const load = useCallback(async () => {
    const response = await apiFetch(`${base}/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ characterId: String(characterId) }) });
    if (!response.ok) throw new Error('Roll requests could not be loaded.');
    const data = await response.json();
    return { requests: data.requests || [], helpable: data.helpable || [] };
  }, [base, characterId]);
  const apply = useCallback(next => {
    // Another character's Improve Fate on this character's roll: noted and logged once (not for ones already there).
    const helped = next.requests.filter(item => item.target.result?.helped);
    if (helpSeen.current) {
      for (const item of helped.filter(entry => !helpSeen.current.has(entry.requestId))) {
        const { helped: help, total, success } = item.target.result;
        notify(`${help.name} used Improve Fate on your ${rollRequestLabel(item.spec)}: ${help.sign < 0 ? '−' : '+'}${help.bonus} · ${total} · ${success ? 'Success' : 'Failure'}`, success ? 'success' : 'info');
        window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: total, source: `Requested · ${rollRequestLabel(item.spec)}`,
          breakdown: rollRequestLogLine(item.spec, item.target, item.target.result) } }));
      }
    }
    helpSeen.current = new Set(helped.map(item => item.requestId));
    setRequests(previous => (JSON.stringify(previous) === JSON.stringify(next.requests) ? previous : next.requests));
    setHelpable(previous => (JSON.stringify(previous) === JSON.stringify(next.helpable) ? previous : next.helpable));
  }, []);
  useCampaignSpellSync({ campaignId: characterId ? campaignId : null, kind: 'rollRequests', load, onData: apply });
  useEffect(() => { evidence.current.clear(); helpSeen.current = null; setRequests([]); setHelpable([]); setDismissed([]); setError(''); }, [campaignId, characterId]);
  const request = requests.find(item => !item.target.result);
  // Improve Fate is offered on another character's failed roll (one at a time; "Not now" hides it here).
  const offer = helpable.find(item => !item.target.result.success && !dismissed.includes(`${item.requestId}:${item.target.characterId}`));
  const helpWithFate = async () => {
    if (!offer || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const what = rollRequestLabel(offer.spec);
    try {
      const response = await apiFetch(`${base}/${encodeURIComponent(offer.requestId)}/fate`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId: offer.target.characterId, helperId: String(characterId), sign: 1 }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Improve Fate could not be used.');
      const { helped: help, total, success } = data.result;
      notify(`Improve Fate on ${offer.target.name}: +${help.bonus} (2d4: ${help.rolls.join(', ')}) · ${total} · ${success ? 'Success' : 'Failure'}`, 'success');
      window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: total, source: `Improve Fate · ${offer.target.name}`,
        breakdown: `+${help.bonus} (2d4: ${help.rolls.join(', ')}) on ${what} · total ${total}${Number.isFinite(offer.spec.dc) ? ` vs DC ${offer.spec.dc}` : ''} · ${success ? 'Success' : 'Failure'}` } }));
      setHelpable(previous => previous.filter(item => item !== offer));
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  const send = async (requestId, body) => {
    const response = await apiFetch(`${base}/${encodeURIComponent(requestId)}/respond`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ characterId: String(characterId), ...body }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(result.message || 'The roll could not be sent.'), { status: response.status });
    return result.result;
  };
  const countercharmContext = (rollRequestId, saverId) => ({ campaignId, source: 'roll-request', rollRequestId, requestId: `roll-request:${rollRequestId}:${saverId}`, saverId });
  const roll = async () => {
    if (!request || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const { requestId, spec, target } = request;
    const what = rollRequestLabel(spec);
    try {
      let body = evidence.current.get(requestId);
      if (!body) {
        const rolled = await rollSkillWithDiceBox(target.bonus, { character, rollMode: target.mode, diceModifiers: target.diceModifiers, rollContext: target.rollContext,
          proficient: target.proficient, source: what, ability: spec.ability, strengthScore: target.strengthScore });
        body = { dice: rolled.rolledD20s, modifierDice: rolled.modifierDice || [] };
        // A known DC: a failed roll offers its rescue before the roll is sent.
        if (Number.isFinite(spec.dc) && (target.automaticFailure || rolled.result < spec.dc)) {
          Object.assign(body, await offerSaveRescue(character, { id: `roll-request:${requestId}`, what, outcome: `${rolled.result} vs DC ${spec.dc}`, save: spec.kind === 'save', countercharm: countercharmContext(requestId, target.characterId) }));
        }
        evidence.current.set(requestId, body);
      }
      let result = await send(requestId, body);
      // A hidden DC: the failure is known from the result; one rescue may follow.
      if (!result.success && !result.rescued && !Number.isFinite(spec.dc) && !target.automaticFailure) {
        const rescue = await offerSaveRescue(character, { id: `roll-request:${requestId}:late`, what, outcome: `total ${result.total}`, save: spec.kind === 'save', countercharm: countercharmContext(requestId, target.characterId) });
        if (Object.keys(rescue).length) result = await send(requestId, rescue);
      }
      evidence.current.delete(requestId);
      // Into the sheet's roll log like any roll (no roll label: rescues were already offered with the request).
      window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: result.total, source: `Requested · ${what}`,
        breakdown: rollRequestLogLine(spec, target, result) } }));
      notify(`${what}: ${result.total}${Number.isFinite(spec.dc) ? ` vs DC ${spec.dc}` : ''} · ${result.success ? 'Success' : 'Failure'}`, result.success ? 'success' : 'info');
      setRequests(previous => previous.map(item => (item.requestId === requestId ? { ...item, target: { ...item.target, result } } : item)));
    } catch (failure) {
      if (failure.status >= 400 && failure.status < 500) evidence.current.delete(requestId);
      setError(failure.message);
    } finally { lock.current = false; setBusy(false); }
  };
  if (!request && offer) {
    return <Modal show centered onHide={() => setDismissed(previous => [...previous, `${offer.requestId}:${offer.target.characterId}`])}
      className="dnd-modal modern-modal roll-request-prompt" aria-label="Improve Fate">
      <Modal.Body>
        <div className="roll-request__row">
          <span className="roll-request__muted"><Dices size={14} aria-hidden="true" /> Improve Fate · Boon of Fate</span>
          <span className="roll-request__pill">{Number.isFinite(offer.spec.dc) ? `DC ${offer.spec.dc}` : 'DC hidden'}</span>
        </div>
        <div className="roll-request__title">{offer.target.name}: {rollRequestLabel(offer.spec)}</div>
        <div className="roll-request__muted">Rolled {offer.target.result.total} · Failure. Add 2d4 to it? It returns when you roll Initiative or finish a rest.</div>
        {error && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{error}</Alert>}
        <div className="roll-request__row mt-2">
          <Button size="sm" variant="outline-light" disabled={busy} onClick={() => setDismissed(previous => [...previous, `${offer.requestId}:${offer.target.characterId}`])}>Not now</Button>
          <Button size="sm" disabled={busy} onClick={helpWithFate}>{busy ? 'Rolling…' : 'Add 2d4'}</Button>
        </div>
      </Modal.Body>
    </Modal>;
  }
  if (!request) return null;
  const { spec, target } = request;
  const mode = target.mode === 'advantage' ? `Advantage (${target.advantageSources.join(', ')})`
    : target.mode === 'disadvantage' ? `Disadvantage (${target.disadvantageSources.join(', ')})` : null;
  const bonus = `${target.bonus >= 0 ? '+' : ''}${target.bonus}${target.proficient ? ' · proficient' : ''}`;
  return <Modal show centered backdrop="static" keyboard={false} className="dnd-modal modern-modal roll-request-prompt" aria-label="Roll request">
    <Modal.Body>
      <div className="roll-request__row">
        <span className="roll-request__muted"><Dices size={14} aria-hidden="true" /> Your DM asks</span>
        <span className="roll-request__pill">{Number.isFinite(spec.dc) ? `DC ${spec.dc}` : 'DC hidden'}</span>
      </div>
      <div className="roll-request__title">{rollRequestLabel(spec)}</div>
      {spec.reason && <div className="roll-request__muted">{spec.reason}</div>}
      {request.group && <div className="roll-request__muted">{groupCheckLine(request.group)}</div>}
      {target.automaticFailure && <div className="roll-request__muted">A condition makes this save fail automatically.</div>}
      {error && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{error}</Alert>}
      <div className="roll-request__row mt-2">
        <span className="roll-request__muted">{bonus}{mode ? ` · ${mode}` : ''}</span>
        <Button size="sm" disabled={busy} onClick={roll}>{busy ? 'Rolling…' : evidence.current.has(request.requestId) ? 'Send again' : 'Roll'}</Button>
      </div>
    </Modal.Body>
  </Modal>;
}
